import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  Button,
  Avatar,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  IconButton,
  Popover,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import {
  Share as ShareIcon,
  ChevronRight as ChevronRightIcon,
  Edit as EditIcon,
  HelpOutline as HelpOutlineIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { Event, SportType, SUPPORTED_SPORTS } from '../types/event';
import { eventApi, requestApi, EventParticipant } from '../utils/api';
import SportsIcon from '../components/SportsIcon';
import { getUserInitials } from '../utils/userUtils';
import LoginModal from '../components/auth/LoginModal';
import SignupModal from '../components/auth/SignupModal';
import ConfirmationModal from '../components/ConfirmationModal';
import CreateEventModal from '../components/CreateEventModal';
import CompleteProfilePrompt from '../components/CompleteProfilePrompt';
import { eventBus, EVENTS } from '../utils/events';
import { getLevelDisplayName, getLevelsForSport } from '../utils/levelDefinitions';

interface EventDetailProps {
  eventId: string;
  onBack: () => void;
}

const EventDetail: React.FC<EventDetailProps> = ({ eventId, onBack }) => {
  const { user, isAuthenticated, isProfileComplete, needsProfileSetup, clearProfileSetup } = useAuth();
  const navigate = useNavigate();
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [joinLoading, setJoinLoading] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('login');
  const [joinSuccess, setJoinSuccess] = useState(false);
  const [myRequestStatus, setMyRequestStatus] = useState<'SUBMITTED' | 'ACCEPTED' | 'DECLINED' | 'CANCELED' | null>(null);
  const [myRequestId, setMyRequestId] = useState<string | null>(null);
  const [actionReady, setActionReady] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [levelHelpAnchor, setLevelHelpAnchor] = useState<HTMLElement | null>(null);
  const [participants, setParticipants] = useState<EventParticipant[]>([]);
  const [profilePromptOpen, setProfilePromptOpen] = useState(false);
  const [editProfileOpen, setEditProfileOpen] = useState(false);

  // Confirmation Modal State
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmConfig, setConfirmConfig] = useState({
    title: '',
    message: '',
    onConfirm: () => { },
    isDestructive: false,
    confirmLabel: 'Confirm'
  });

  useEffect(() => {
    loadEventDetail();
  }, [eventId, isAuthenticated]);

  const loadEventDetail = async () => {
    try {
      setLoading(true);
      setActionReady(false);
      const event = await eventApi.getEventById(eventId);
      setEvent(event);
      setError(null);

      try {
        const participantList = await eventApi.getEventParticipants(eventId);
        setParticipants(participantList);
      } catch {
        setParticipants([]);
      }

      // Fetch my request status for this event, if logged in
      if (isAuthenticated) {
        try {
          const requests = await requestApi.getUserRequests();
          const mine = requests.find(r => r.event_id === eventId);
          setMyRequestStatus(mine ? (mine.status as any) : null);
          setMyRequestId(mine ? mine.id : null);
        } catch (e) {
          // Ignore request fetch errors for UI; keep default
        }
      } else {
        setMyRequestStatus(null);
        setMyRequestId(null);
      }
    } catch (err: any) {
      console.error('Error loading event:', err);
      if (err.response?.status === 401) {
        setShowAuthModal(true);
        setError(null);
      } else if (err.response?.status === 404) {
        setError('Event not found');
      } else {
        setError('Failed to load event details');
      }
    } finally {
      setLoading(false);
      setActionReady(true);
    }
  };

  const handleJoinEvent = async () => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }

    if (!isProfileComplete) {
      setProfilePromptOpen(true);
      return;
    }

    try {
      setJoinLoading(true);
      await requestApi.createRequest(eventId, !!event?.auto_accept);
      setJoinSuccess(true);
      // Reflect immediate status in UI
      setMyRequestStatus(event?.auto_accept ? 'ACCEPTED' : 'SUBMITTED');

      // Refresh requests to get the ID
      const requests = await requestApi.getUserRequests();
      const mine = requests.find(r => r.event_id === eventId);
      if (mine) setMyRequestId(mine.id);

      // Notify other components
      eventBus.emit(EVENTS.EVENT_UPDATED);

    } catch (err: any) {
      console.error('Error joining event:', err);
      if (err.response?.status === 409 || err.response?.status === 400) {
        // Already requested; refresh status
        try {
          const requests = await requestApi.getUserRequests();
          const mine = requests.find(r => r.event_id === eventId);
          if (mine) setMyRequestStatus(mine.status as any);
          if (mine) setMyRequestId(mine.id);
        } catch { }
        setError('You have already requested to join this event.');
      } else {
        setError('Failed to join event. Please try again.');
      }
    } finally {
      setJoinLoading(false);
    }
  };

  const handleCancelEvent = () => {
    if (!event) return;

    setConfirmConfig({
      title: 'Cancel Event',
      message: 'Are you sure you want to cancel this event? This action cannot be undone.',
      confirmLabel: 'Yes, Cancel Event',
      isDestructive: true,
      onConfirm: async () => {
        try {
          await eventApi.cancelEvent(event.id);
          setEvent({ ...event, status: 'CANCELED' });
          eventBus.emit(EVENTS.EVENT_UPDATED);
          setConfirmOpen(false);
        } catch (error) {
          console.error('Error canceling event:', error);
          alert('Failed to cancel event.');
        }
      }
    });
    setConfirmOpen(true);
  };

  const handleCancelRequest = () => {
    if (!myRequestId) return;

    // Check deadline if accepted
    if (myRequestStatus === 'ACCEPTED' && event?.cancellation_deadline_hours) {
      const start = new Date(event.starts_at);
      const now = new Date();
      const hoursDiff = (start.getTime() - now.getTime()) / (1000 * 60 * 60);

      if (hoursDiff < event.cancellation_deadline_hours) {
        alert(`You cannot cancel less than ${event.cancellation_deadline_hours} hours before the event.`);
        return;
      }
    }

    const isLeaving = myRequestStatus === 'ACCEPTED';

    setConfirmConfig({
      title: isLeaving ? 'Leave Event' : 'Cancel Request',
      message: isLeaving ? 'Are you sure you want to leave this event?' : 'Are you sure you want to cancel your request?',
      confirmLabel: isLeaving ? 'Leave Event' : 'Cancel Request',
      isDestructive: true,
      onConfirm: async () => {
        try {
          await requestApi.cancelRequest(myRequestId);
          setMyRequestStatus('CANCELED');
          eventBus.emit(EVENTS.EVENT_UPDATED);
          setConfirmOpen(false);
        } catch (error) {
          console.error('Error canceling request:', error);
          alert('Failed to cancel request.');
        }
      }
    });
    setConfirmOpen(true);
  };

  const handleAuthSuccess = () => {
    setShowAuthModal(false);
    setTimeout(() => {
      loadEventDetail();
    }, 500);
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: event?.title,
        text: event?.description,
        url: window.location.href,
      }).catch((err) => console.log('Error sharing:', err));
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  // Loading state
  if (loading && !showAuthModal) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: 'white' }}>
        <CircularProgress sx={{ color: '#533B7D' }} />
      </Box>
    );
  }

  // Auth required state
  if (showAuthModal && !event) {
    return (
      <Box sx={{ bgcolor: 'white', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Container maxWidth="sm">
          <Box sx={{ p: 4, borderRadius: 3, textAlign: 'center', bgcolor: 'white', boxShadow: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
              Sign In Required
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, color: '#666' }}>
              Please sign in to view event details and join events.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="outlined"
                onClick={() => setAuthMode('login')}
                sx={{ borderColor: '#533B7D', color: '#533B7D' }}
              >
                Sign In
              </Button>
              <Button
                variant="contained"
                onClick={() => setAuthMode('signup')}
                sx={{ backgroundColor: '#533B7D' }}
              >
                Sign Up
              </Button>
            </Box>
          </Box>

          <LoginModal
            open={showAuthModal && authMode === 'login'}
            onClose={() => setShowAuthModal(false)}
            onSwitchToSignup={() => setAuthMode('signup')}
            onLoginSuccess={handleAuthSuccess}
          />

          <SignupModal
            open={showAuthModal && authMode === 'signup'}
            onClose={() => {
              setShowAuthModal(false);
              if (needsProfileSetup) {
                clearProfileSetup();
              }
            }}
            onSwitchToLogin={() => setAuthMode('login')}
            onSignupSuccess={() => {
              if (needsProfileSetup) {
                clearProfileSetup();
              }
              handleAuthSuccess();
            }}
          />

          <ConfirmationModal
            open={confirmOpen}
            title={confirmConfig.title}
            message={confirmConfig.message}
            confirmLabel={confirmConfig.confirmLabel}
            isDestructive={confirmConfig.isDestructive}
            onConfirm={confirmConfig.onConfirm}
            onCancel={() => setConfirmOpen(false)}
          />
        </Container>
      </Box>
    );
  }

  // Error state
  if (error || !event) {
    return (
      <Box sx={{ bgcolor: 'white', minHeight: '100vh', pt: 2 }}>
        <Container maxWidth="lg">
          <Alert severity="error" sx={{ mb: 2 }}>
            {error || 'Event not found'}
          </Alert>
          <Button onClick={onBack} sx={{ color: '#533B7D' }}>
            Back to Events
          </Button>
        </Container>
      </Box>
    );
  }

  const isEventCreator = user && event.created_by === user.id;

  // Format date and time
  const eventDate = new Date(event.starts_at);
  const formattedDate = eventDate.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });

  const formattedTime = eventDate.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });

  return (
    <Box sx={{ bgcolor: 'white', minHeight: '100vh', pb: { xs: '80px', md: '100px' } }}>
      <Container
        maxWidth={false}
        sx={{
          width: { xs: '100%', lg: '1440px' },
          maxWidth: '100%',
          px: { xs: 2, sm: 3, md: 4, lg: '240px' }
        }}
      >
        {/* Main Content Container */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>

          {/* Success Alert */}
          {joinSuccess && (
            <Alert severity="success">
              {event?.auto_accept
                ? "You've successfully joined the event!"
                : "Request sent successfully! The event host will review your request."}
            </Alert>
          )}

          {/* Cancellation Banner */}
          {event.status === 'CANCELED' && (
            <Alert severity="error" icon={false} sx={{ fontWeight: 'bold' }}>
              This event has been canceled by the host.
            </Alert>
          )}

          {/* Breadcrumbs */}
          <Breadcrumbs separator={<ChevronRightIcon fontSize="small" sx={{ color: '#737373' }} />}>
            <Link
              component="button"
              variant="body2"
              onClick={onBack}
              sx={{
                color: '#737373',
                textDecoration: 'none',
                fontSize: '14px',
                fontFamily: '"Nunito Sans", sans-serif',
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              Home
            </Link>
            <Typography
              sx={{
                color: '#000',
                fontSize: '14px',
                fontFamily: '"Nunito Sans", sans-serif'
              }}
            >
              {event.title}
            </Typography>
          </Breadcrumbs>

          {/* Header Section */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between', 
            alignItems: { xs: 'flex-start', md: 'flex-start' },
            gap: { xs: 2, md: 0 }
          }}>
            {/* Left: Title, Date, Share */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: '12px', md: '16px' }, flex: 1 }}>
              {/* Title Row with Badge */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: { xs: '8px', md: '16px' },
                flexWrap: 'wrap'
              }}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '20px', sm: '24px', md: '28px' },
                    lineHeight: { xs: '28px', sm: '32px', md: '36px' },
                    color: '#000',
                    fontFamily: '"Nunito Sans", sans-serif'
                  }}
                >
                  {event.title}
                </Typography>

                {event.activity_type && (
                  <Box
                    sx={{
                      bgcolor: '#533b7d',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      px: '12px',
                      py: '8px',
                      borderRadius: '4px'
                    }}
                  >
                    <SportsIcon sport={event.activity_type as SportType} size={16} />
                    <Typography
                      sx={{
                        color: 'white',
                        fontSize: '11px',
                        fontWeight: 500,
                        lineHeight: '16px',
                        fontFamily: '"Nunito Sans", sans-serif'
                      }}
                    >
                      {event.activity_type}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Date and Time Row */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: { xs: '12px', sm: '16px', md: '24px' },
                flexWrap: 'wrap'
              }}>
                <Typography
                  sx={{
                    fontSize: { xs: '16px', sm: '18px', md: '22px' },
                    lineHeight: { xs: '24px', sm: '26px', md: '28px' },
                    color: '#6a6a6a',
                    fontFamily: '"Nunito Sans", sans-serif'
                  }}
                >
                  {formattedDate}
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: '16px', sm: '18px', md: '22px' },
                    lineHeight: { xs: '24px', sm: '26px', md: '28px' },
                    color: '#6a6a6a',
                    fontFamily: '"Nunito Sans", sans-serif'
                  }}
                >
                  {formattedTime}
                </Typography>
              </Box>

              {/* Share Button */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <ShareIcon sx={{ fontSize: '24px', color: '#000' }} />
                <Button
                  onClick={handleShare}
                  sx={{
                    color: '#000',
                    textTransform: 'none',
                    fontSize: '16px',
                    fontWeight: 400,
                    lineHeight: '24px',
                    textDecoration: 'underline',
                    padding: 0,
                    minWidth: 'auto',
                    fontFamily: '"Nunito Sans", sans-serif',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      textDecoration: 'underline'
                    }
                  }}
                >
                  Share
                </Button>
              </Box>
            </Box>

            {/* Right: Request to Join or Edit Button */}
            {actionReady && (
              isEventCreator ? (
                <Box sx={{ 
                  display: 'flex', 
                  gap: { xs: 1, md: 2 },
                  flexDirection: { xs: 'column', sm: 'row' },
                  width: { xs: '100%', md: 'auto' },
                  mt: { xs: 2, md: 0 }
                }}>
                  {event.status !== 'CANCELED' && (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={handleCancelEvent}
                    >
                      Cancel Event
                    </Button>
                  )}
                  {event.status === 'CANCELED' && (
                    <Button
                      variant="outlined"
                      color="error"
                      disabled
                    >
                      Canceled
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={() => setShowEditModal(true)}
                    disabled={event.status === 'CANCELED'}
                    sx={{
                      backgroundColor: '#533B7D',
                      color: 'white',
                      textTransform: 'none',
                      fontWeight: 500,
                      '&:disabled': {
                        backgroundColor: '#ccc'
                      },
                      '&:hover': {
                        backgroundColor: '#422F64'
                      }
                    }}
                  >
                    Edit Event
                  </Button>
                </Box>
              ) : myRequestStatus === 'ACCEPTED' ? (
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Button
                    variant="contained"
                    disabled
                    sx={{
                      background: '#4caf50',
                      color: 'white',
                      textTransform: 'none',
                      fontWeight: 600
                    }}
                  >
                    Joined
                  </Button>
                  {event.status !== 'CANCELED' && (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={handleCancelRequest}
                      sx={{ textTransform: 'none' }}
                    >
                      Leave Event
                    </Button>
                  )}
                </Box>
              ) : myRequestStatus === 'SUBMITTED' ? (
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Button
                    variant="contained"
                    disabled
                    sx={{
                      background: '#9e9e9e',
                      color: 'white',
                      textTransform: 'none',
                      fontWeight: 600
                    }}
                  >
                    Request Sent
                  </Button>
                  {event.status !== 'CANCELED' && (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={handleCancelRequest}
                      sx={{ textTransform: 'none' }}
                    >
                      Cancel Request
                    </Button>
                  )}
                </Box>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleJoinEvent}
                  disabled={joinLoading || joinSuccess || event.status === 'CANCELED'}
                  sx={{
                    backgroundColor: joinSuccess
                      ? '#4caf50'
                      : event?.auto_accept
                        ? '#4caf50'
                        : '#533B7D',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '14px',
                    lineHeight: '20px',
                    textTransform: 'none',
                    px: 2,
                    py: '10px',
                    height: '48px',
                    borderRadius: '8px',
                    boxShadow: 'none',
                    fontFamily: '"Inter", sans-serif',
                    '&:hover': {
                      backgroundColor: joinSuccess
                        ? '#45a049'
                        : event?.auto_accept
                          ? '#45a049'
                          : '#422F64',
                      boxShadow: 'none'
                    },
                    '&:disabled': {
                      backgroundColor: '#ccc',
                      color: '#666'
                    }
                  }}
                >
                  {joinLoading
                    ? (event?.auto_accept ? 'Joining...' : 'Requesting...')
                    : joinSuccess
                      ? (event?.auto_accept ? 'Joined!' : 'Request Sent!')
                      : (event?.auto_accept ? '🎉 Join Now (Free)' : 'Request to Join')
                  }
                </Button>
              )
            )}
          </Box>

          {/* Image Gallery */}
          {event.images?.image_1 && (
            <Box
              sx={{
                height: { xs: '200px', sm: '250px', md: '320px' },
                overflow: 'hidden',
                borderRadius: '8px',
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: { xs: '4px', md: '8px' },
                width: '100%'
              }}
            >
              {/* Large Image - Left (480px) */}
              <Box
                sx={{
                  width: { xs: '100%', md: '480px' },
                  height: { xs: '100%', md: '320px' },
                  flexShrink: 0,
                  overflow: 'hidden',
                  borderRadius: '8px',
                  bgcolor: '#f5f5f5'
                }}
              >
                <img
                  src={event.images.image_1}
                  alt={event.title}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: '50% 50%'
                  }}
                />
              </Box>

              {/* Small Images Grid - Right */}
              {(event.images?.image_2 || event.images?.image_3) && (
                <Box sx={{ 
                  flex: 1, 
                  display: { xs: 'none', md: 'flex' },
                  flexDirection: 'column', 
                  gap: '8px' 
                }}>
                  {/* Top Row */}
                  <Box sx={{ display: 'flex', gap: '8px', height: '156px' }}>
                    {event.images?.image_2 && (
                      <Box sx={{ width: '240px', height: '156px', borderRadius: '8px', overflow: 'hidden', bgcolor: '#f5f5f5' }}>
                        <img
                          src={event.images.image_2}
                          alt={`${event.title} 2`}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      </Box>
                    )}
                    {event.images?.image_3 && (
                      <Box sx={{ flex: 1, height: '156px', borderRadius: '8px', overflow: 'hidden', bgcolor: '#f5f5f5' }}>
                        <img
                          src={event.images.image_3}
                          alt={`${event.title} 3`}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      </Box>
                    )}
                  </Box>

                  {/* Bottom Row - Only show if there are images */}
                  {(event.images?.image_2 || event.images?.image_3) && (
                    <Box sx={{ display: 'flex', gap: '8px', flex: 1 }}>
                      <Box sx={{ width: '240px', borderRadius: '8px', bgcolor: '#f5f5f5' }} />
                      <Box sx={{ flex: 1, borderRadius: '8px', bgcolor: '#f5f5f5' }} />
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          )}

          {/* Content Sections */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '48px' }}>

            {/* Host & Description Section */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {/* Host */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <Typography
                  sx={{
                    fontSize: '12px',
                    lineHeight: '16px',
                    color: '#6a6a6a',
                    textTransform: 'uppercase',
                    fontFamily: '"Nunito Sans", sans-serif'
                  }}
                >
                  HOST
                </Typography>
                <Box 
                  onClick={() => {
                    // If clicking on own profile, go to /profile instead of /user/{id}
                    if (user && event.created_by === user.id) {
                      navigate('/profile');
                    } else {
                      navigate(`/user/${event.created_by}`);
                    }
                  }}
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '16px',
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease',
                    '&:hover': {
                      transform: 'translateX(4px)',
                      '& .host-name': {
                        color: '#533B7D'
                      }
                    }
                  }}
                >
                  <Avatar
                    src={event.host_profile_picture || undefined}
                    sx={{
                      width: 40,
                      height: 40,
                      backgroundColor: event.host_profile_picture ? undefined : '#E8DDFF',
                      color: event.host_profile_picture ? undefined : '#533B7D',
                      fontSize: '16px',
                      fontWeight: 500
                    }}
                  >
                    {!event.host_profile_picture && getUserInitials(event.host_name || 'Host')}
                  </Avatar>
                  <Typography
                    className="host-name"
                    sx={{
                      fontSize: '16px',
                      fontWeight: 500,
                      lineHeight: '24px',
                      color: '#000',
                      fontFamily: '"Nunito Sans", sans-serif',
                      transition: 'color 0.2s ease'
                    }}
                  >
                    {event.host_name || 'Event Host'}
                  </Typography>
                </Box>
              </Box>

              {/* Participants (joined guests; host is shown in HOST section above) */}
              {participants.filter((p) => p.role === 'guest').length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <Typography
                    sx={{
                      fontSize: '12px',
                      lineHeight: '16px',
                      color: '#6a6a6a',
                      textTransform: 'uppercase',
                      fontFamily: '"Nunito Sans", sans-serif'
                    }}
                  >
                    PARTICIPANTS ({participants.filter((p) => p.role === 'guest').length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
                    {participants
                      .filter((p) => p.role === 'guest')
                      .map((p) => (
                        <Box
                          key={p.user_id}
                          onClick={() => {
                            if (user && p.user_id === user.id) {
                              navigate('/profile');
                            } else {
                              navigate(`/user/${p.user_id}`);
                            }
                          }}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            cursor: 'pointer',
                            transition: 'transform 0.2s ease',
                            '&:hover': {
                              transform: 'translateX(4px)',
                              '& .participant-name': {
                                color: '#533B7D'
                              }
                            }
                          }}
                        >
                          <Avatar
                            src={p.profile_picture || undefined}
                            sx={{
                              width: 40,
                              height: 40,
                              backgroundColor: p.profile_picture ? undefined : '#E8DDFF',
                              color: p.profile_picture ? undefined : '#533B7D',
                              fontSize: '16px',
                              fontWeight: 500
                            }}
                          >
                            {!p.profile_picture && getUserInitials(p.display_name)}
                          </Avatar>
                          <Typography
                            className="participant-name"
                            sx={{
                              fontSize: '16px',
                              fontWeight: 500,
                              lineHeight: '24px',
                              color: '#000',
                              fontFamily: '"Nunito Sans", sans-serif',
                              transition: 'color 0.2s ease'
                            }}
                          >
                            {p.display_name}
                          </Typography>
                        </Box>
                      ))}
                  </Box>
                </Box>
              )}

              {/* Description */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Typography
                  sx={{
                    fontSize: '12px',
                    lineHeight: '16px',
                    color: '#6a6a6a',
                    textTransform: 'uppercase',
                    fontFamily: '"Nunito Sans", sans-serif'
                  }}
                >
                  DESCRIPTION
                </Typography>
                <Typography
                  sx={{
                    fontSize: '16px',
                    lineHeight: '24px',
                    color: '#000',
                    maxWidth: '960px',
                    fontFamily: '"Nunito Sans", sans-serif',
                    whiteSpace: 'pre-wrap'
                  }}
                >
                  {event.description || 'No description provided.'}
                </Typography>
              </Box>
            </Box>

            {/* When & Where Section */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {/* Section Header */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <Typography
                  sx={{
                    fontSize: '16px',
                    fontWeight: 600,
                    lineHeight: '24px',
                    color: '#000',
                    fontFamily: '"Nunito Sans", sans-serif'
                  }}
                >
                  When & Where
                </Typography>
                <Divider sx={{ borderColor: '#cac4d0' }} />
              </Box>

              {/* Two Column Layout */}
              <Box sx={{ display: 'flex', gap: '24px' }}>
                {/* Left Column - Location */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <Typography
                    sx={{
                      fontSize: '12px',
                      lineHeight: '16px',
                      color: '#6a6a6a',
                      textTransform: 'uppercase',
                      fontFamily: '"Nunito Sans", sans-serif'
                    }}
                  >
                    LOCATION
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '16px',
                      fontWeight: 700,
                      lineHeight: '24px',
                      color: '#000',
                      fontFamily: '"Nunito Sans", sans-serif'
                    }}
                  >
                    {event.location || 'Location TBD'}
                  </Typography>
                  {(event.formatted_address || event.address) && (
                    <Link
                      href={
                        event.latitude && event.longitude
                          ? `https://www.google.com/maps/search/?api=1&query=${event.latitude},${event.longitude}`
                          : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.formatted_address || event.address || '')}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '16px',
                          lineHeight: '24px',
                          color: '#000',
                          fontFamily: '"Nunito Sans", sans-serif'
                        }}
                      >
                        {event.formatted_address || event.address}
                      </Typography>
                    </Link>
                  )}
                </Box>

                {/* Right Column - Date & Time */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '24px' }}>
                  {/* Date */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <Typography
                      sx={{
                        fontSize: '12px',
                        lineHeight: '16px',
                        color: '#6a6a6a',
                        textTransform: 'uppercase',
                        fontFamily: '"Nunito Sans", sans-serif'
                      }}
                    >
                      DATE
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '16px',
                        lineHeight: '24px',
                        color: '#000',
                        fontFamily: '"Nunito Sans", sans-serif'
                      }}
                    >
                      {eventDate.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </Typography>
                  </Box>

                  {/* Time */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <Typography
                      sx={{
                        fontSize: '12px',
                        lineHeight: '16px',
                        color: '#6a6a6a',
                        textTransform: 'uppercase',
                        fontFamily: '"Nunito Sans", sans-serif'
                      }}
                    >
                      TIME
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '16px',
                        lineHeight: '24px',
                        color: '#000',
                        fontFamily: '"Nunito Sans", sans-serif'
                      }}
                    >
                      {formattedTime}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Player Requirements Section */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Section Header */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <Typography
                    sx={{
                      fontSize: '16px',
                      fontWeight: 600,
                      lineHeight: '24px',
                      color: '#000',
                      fontFamily: '"Nunito Sans", sans-serif'
                    }}
                  >
                    Player Requirements
                  </Typography>
                  <Divider sx={{ borderColor: '#cac4d0' }} />
                </Box>

                {/* Two Column Layout */}
                <Box sx={{ display: 'flex', gap: '24px' }}>
                  {/* Number of Players */}
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <Typography
                      sx={{
                        fontSize: '12px',
                        lineHeight: '16px',
                        color: '#6a6a6a',
                        textTransform: 'uppercase',
                        fontFamily: '"Nunito Sans", sans-serif'
                      }}
                    >
                      NUMBER OF PLAYERS
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '16px',
                        lineHeight: '24px',
                        color: '#000',
                        fontFamily: '"Nunito Sans", sans-serif'
                      }}
                    >
                      {event.occupied_spots} / {event.capacity} Players
                    </Typography>
                  </Box>

                  {/* Level Requirements */}
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography
                        sx={{
                          fontSize: '12px',
                          lineHeight: '16px',
                          color: '#6a6a6a',
                          textTransform: 'uppercase',
                          fontFamily: '"Nunito Sans", sans-serif'
                        }}
                      >
                        LEVEL REQUIREMENTS
                      </Typography>
                      {event.activity_type && (
                        <IconButton
                          size="small"
                          onClick={(e) => setLevelHelpAnchor(e.currentTarget)}
                          sx={{ color: '#6a6a6a', p: 0.5, ml: 0.5 }}
                        >
                          <HelpOutlineIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      )}
                    </Box>
                    <Typography
                      sx={{
                        fontSize: '16px',
                        lineHeight: '24px',
                        color: '#000',
                        fontFamily: '"Nunito Sans", sans-serif'
                      }}
                    >
                      {event.level_requirement && event.activity_type
                        ? getLevelDisplayName(event.level_requirement, event.activity_type)
                        : event.level_needed || 'Any Level'}
                    </Typography>
                  </Box>
                </Box>

                {/* Tags */}
                {event.tags && event.tags.length > 0 && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <Typography
                      sx={{
                        fontSize: '12px',
                        lineHeight: '16px',
                        color: '#6a6a6a',
                        textTransform: 'uppercase',
                        fontFamily: '"Nunito Sans", sans-serif'
                      }}
                    >
                      TAGS
                    </Typography>
                    <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {event.tags.map((tag) => (
                        <Box
                          key={tag.id}
                          sx={{
                            border: '1px solid #e3e3e3',
                            borderRadius: '4px',
                            px: '12px',
                            py: '6px'
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: '11px',
                              fontWeight: 500,
                              lineHeight: '16px',
                              color: '#000',
                              fontFamily: '"Nunito Sans", sans-serif'
                            }}
                          >
                            {tag.name}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>

          </Box>
        </Box>
      </Container>

      {/* Login/Signup Modals */}
      <LoginModal
        open={showAuthModal && authMode === 'login'}
        onClose={() => setShowAuthModal(false)}
        onSwitchToSignup={() => setAuthMode('signup')}
        onLoginSuccess={handleAuthSuccess}
      />

      <SignupModal
        open={showAuthModal && authMode === 'signup'}
        onClose={() => {
          setShowAuthModal(false);
          if (needsProfileSetup) {
            clearProfileSetup();
          }
        }}
        onSwitchToLogin={() => setAuthMode('login')}
        onSignupSuccess={() => {
          if (needsProfileSetup) {
            clearProfileSetup();
          }
          handleAuthSuccess();
        }}
      />

      <ConfirmationModal
        open={confirmOpen}
        title={confirmConfig.title}
        message={confirmConfig.message}
        confirmLabel={confirmConfig.confirmLabel}
        isDestructive={confirmConfig.isDestructive}
        onConfirm={confirmConfig.onConfirm}
        onCancel={() => setConfirmOpen(false)}
      />

      <CreateEventModal
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        editMode={true}
        existingEvent={event}
        onEventCreated={() => {
          loadEventDetail();
          setShowEditModal(false);
        }}
      />

      <CompleteProfilePrompt
        open={profilePromptOpen}
        onClose={() => setProfilePromptOpen(false)}
        onComplete={() => {
          setProfilePromptOpen(false);
          setEditProfileOpen(true);
        }}
        actionLabel="join this event"
      />

      <SignupModal
        open={editProfileOpen}
        onClose={() => setEditProfileOpen(false)}
        onSignupSuccess={() => {
          setEditProfileOpen(false);
          handleJoinEvent();
        }}
        editMode
        userData={user || undefined}
      />

      {/* Level Help Popover */}
      {event && event.activity_type && (
        <Popover
          open={Boolean(levelHelpAnchor)}
          anchorEl={levelHelpAnchor}
          onClose={() => setLevelHelpAnchor(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <Box sx={{ p: 2, maxWidth: 400 }}>
            <Typography variant="h6" gutterBottom>
              Level Guide - {event.activity_type}
            </Typography>
            {event.activity_type ? (
              (() => {
                const normalizedSport = SUPPORTED_SPORTS.find(s => s.toLowerCase() === event.activity_type?.toLowerCase());
                const levels = normalizedSport ? getLevelsForSport(normalizedSport as SportType) : [];
                if (levels.length === 0) {
                  return (
                    <Typography variant="body2" color="text.secondary">
                      No level information available for {event.activity_type}
                    </Typography>
                  );
                }
                return (
                  <List dense>
                    {levels.map((level, index) => (
                      <React.Fragment key={level.id}>
                        <ListItem>
                          <ListItemText
                            primary={
                              <Box>
                                <Typography variant="subtitle2" component="span" fontWeight="bold">
                                  {level.range}
                                </Typography>
                                <Typography variant="caption" component="span" sx={{ ml: 1, color: 'text.secondary' }}>
                                  ({level.genericName})
                                </Typography>
                              </Box>
                            }
                            secondary={
                              <Box sx={{ mt: 0.5 }}>
                                <Typography variant="body2">{level.description}</Typography>
                                {level.type && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                    {level.type}
                                  </Typography>
                                )}
                                {level.description2 && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                    {level.description2}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                        {index < levels.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                );
              })()
            ) : (
              <Typography variant="body2" color="text.secondary">
                Please select a sport first
              </Typography>
            )}
          </Box>
        </Popover>
      )}
    </Box>
  );
};

export default EventDetail;
